import { ExtendedCarItem } from '@/features/cars/types';
import {
  ConstructorConfig,
  ConstructorItem,
} from '@/features/config/hooks/useFilterConstructor';

/**
 * Исключает из конфигурации сетки блоки, которые необходимо скрыть.
 * Критерием для скрытия блока является опциональная функция skipRenderFn,
 * которая может быть задана в конфигурации.
 * Если функция присутствует в конфигурации блока и возвращает true,
 * то блок будет исключен из конфигурации на отрисовку.
 * @param gridConfig конфигурация сетки
 * @param carItem опциональный параметр для функции skipRenderFn.
 * @returns Конфигурация сетки, исключающая отфильтрованные с помощью skipRenderFn.
 */
export function filterHiddenBlocks<T extends ConstructorConfig>(
  gridConfig: T,
  carItem?: ExtendedCarItem,
): T {
  if (!gridConfig.items) {
    return gridConfig;
  }

  const gridConfigCopy = { ...gridConfig };
  if (Array.isArray(gridConfigCopy.items)) {
    gridConfigCopy.items = filterSkippedItems(gridConfigCopy.items, carItem);
  }

  return gridConfigCopy;
}

/**
 * Рекурсивно исключает блоки, skipRenderFn которых возвращает true.
 * @param items Элементы сетки.
 * @param carItem параметр функции skipRenderFn
 * @returns возвращает новый массив элементов сетки, без элементов, которые не нужно рендерить.
 */
function filterSkippedItems(
  items: Array<ConstructorItem>,
  carItem?: ExtendedCarItem,
): Array<ConstructorItem> {
  const filteredItems: Array<ConstructorItem> = [];

  items.forEach(item => {
    const isSkipItem =
      'skipRenderFn' in item &&
      typeof item.skipRenderFn === 'function' &&
      item.skipRenderFn(carItem);

    if (isSkipItem) {
      return;
    }

    const filteredItem = { ...item };

    if ('items' in filteredItem) {
      filteredItem.items = filterSkippedItems(
        filteredItem.items as Array<ConstructorItem>,
        carItem,
      );
    }

    filteredItems.push(filteredItem);
  });

  return filteredItems;
}

export default filterHiddenBlocks;
